import * as moment from 'moment';
import { FormatDatePipe } from '../utils/pipes';
import { Address, mapAddress } from './address.model';
import { YesOrNo } from './common.model';

// Legacy PWP dashboard endpoint statuses
export enum PwpOrderStatus {
  Authorize = 'Authorize',
  InProcess = 'In Process',
  PreAuthInProcess = 'Pre-Auth in Process',
  DocumentationInProcess = 'Documentation in Process',
  InsuranceVerification = 'Insurance Verification',
  OrderPending = 'Order Pending',
  ReadyToShip = 'Ready to Ship',
  Shipped = 'Shipped',
  Delivered = 'Delivered',
}

// New order status endpoint (one dot) statuses
// See https://bitbucket.ccsmed.com/projects/CCLS/repos/ipims-order-status-component/browse/src/main/scala/com/ccsmed/ipims/status/constants/OneDotStatus.scala?at=refs%2Fheads%2Fdevelopment
export enum OneDotOrderStatus {
  Delivered = 'Delivered',
  Shipped = 'Shipped',
  TooEarlyToAuthorize = 'Too Early to Authorize',
  ReadyToAuthorize = 'Ready to Authorize',
  InsuranceInfoNeeded = 'Insurance Info Needed',
  InsuranceVerInProcess = 'Insurance Verification in Process', // OS team needs to fix letter casing
  DocumentationInProcess = 'Documentation in Process', // OS team needs to fix letter casing
  DocumentationNeeded = 'Documentation Needed',
  ReadyToShip = 'Ready to Ship',
  InProcess = 'In Process', // OS team needs to remove either in process or processing status
  Processing = 'Processing',
  DocumentationInReview = 'Documentation in Review',
  PaymentRequired = 'Payment Required',
}

// New order status endpoint (five dot) status names
export enum DetailedOrderStatusName {
  Documentation = 'Documentation',
  PreAuth = 'PreAuth',
  Insurance = 'Insurance',
  Consent = 'Consent',
  Shipment = 'Shipment',
}

export enum ConsentStatusValue {
  NeedsReconsent = 'Needs Re-consent',
}

// API Models

// Legacy PWP dashboard endpoint order item response
export interface PwpApiOrderResponse {
  referenceNumber: number;
  status?: PwpOrderStatus;
  targetedShipDate?: string;
  consentDate?: string;
  deliveryDate?: string;
  trackingNumber?: string;
  trackingNumberUrl?: string;
  shipmentTypeId?: number;
  shipmentTypeName?: string;
  shippingAddressId?: number;
  shippingAddress1?: string;
  shippingAddress2?: string;
  shippingCity?: string;
  shippingZip?: string;
  shippingState?: string;
  mailClass?: string;
  isShipment?: boolean;
  reorderCardId?: number;
  shipmentDescription?: string;
  generationType?: string;
  orderType?: string;
}

// New order status endpoint order item response
export interface OrderStatusApiOrderResponse {
  cardReferenceNumber?: number;
  shipmentReferenceNumber?: number;
  oneDotStatus?: OneDotOrderStatus;
  targetedShipDate?: string;
  deliveryDate?: string;
  consentDate?: string | null;
  estimatedEarliestConsentDate?: string | null;
  trackingNumber?: string;
  trackingNumberUrl?: string;
  shipmentTypeId?: number;
  shipmentTypeName?: string;
  shippingAddressId?: number;
  shippingAddress1?: string;
  shippingAddress2?: string;
  shippingCity?: string;
  shippingZip?: string;
  shippingState?: string;
  mailClass?: string;
  fiveDotStatus: {
    statusName?: DetailedOrderStatusName;
    statusFlag?: YesOrNo;
    internalDetail?: string;
    externalDetail?: string;
  }[];
  orderType?: string;
  isShipment?: boolean;
  shipmentDescription?: string;
  generationType?: string;
}

// Front End Models

export interface OrderDetailedStatus {
  name: DetailedOrderStatusName;
  value?: string;
  description?: string;
  isCompleted: boolean;
}

export interface Order {
  id: number;
  status?: PwpOrderStatus | OneDotOrderStatus;
  statusDescription?: string;
  detailedStatus?: OrderDetailedStatus[];
  targetedShipDate?: string;
  deliveryDate?: string;
  estimatedEarliestConsentDate?: string;
  trackingNumber?: string;
  trackingNumberUrl?: string;
  shipmentTypeId?: number;
  shipmentTypeName?: string;
  shippingAddress?: Address;
  mailClass?: string;
  isShipment?: boolean;
  originatingReorderId?: number;
  // Computed fields
  needsAuthorization: boolean;
  needsReconsent: boolean;
  isFutureConsent: boolean;
  isFutureOrder: boolean;
  isShipped: boolean;
  isDelivered: boolean;
  shipmentDescription?: string;
  generationType?: string;
  orderType?: string;
}

// Mappers

const twoYearsFromNow = moment().add(2, 'years').startOf('day');

const isTwoYearsFromNow = (date?: string | null) => !!date && moment(date).isAfter(twoYearsFromNow, 'day');

const formatDatePipe = new FormatDatePipe();

const computeStatusDescription = (order: Order): string | undefined => {
  switch (order.status) {
    case PwpOrderStatus.OrderPending:
    case OneDotOrderStatus.TooEarlyToAuthorize:
      return `Your order will be ready to authorize
      ${
        order.isFutureConsent || !order.estimatedEarliestConsentDate
          ? ' in the future'
          : ' on ' + formatDatePipe.transform(order.estimatedEarliestConsentDate)
      }. Please come back to authorize order.`;
    case OneDotOrderStatus.InsuranceInfoNeeded:
    case OneDotOrderStatus.InsuranceVerInProcess:
      const insuranceDetailedStatus = order.detailedStatus?.find((ds) => ds.name === DetailedOrderStatusName.Insurance);
      return insuranceDetailedStatus?.description;
    case OneDotOrderStatus.DocumentationInProcess:
    case OneDotOrderStatus.DocumentationNeeded:
      const documentationDetailedStatus = order.detailedStatus?.find(
        (ds) => ds.name === DetailedOrderStatusName.Documentation
      );
      return documentationDetailedStatus?.description;
    case OneDotOrderStatus.DocumentationInReview:
      return 'We are reviewing the paperwork required by your insurance to process your order. We will follow up if we need more information and keep you updated on the status.';
    case OneDotOrderStatus.ReadyToAuthorize:
      if (order.orderType === 'WORKFLOW_ORDER') {
        return 'Please contact us at 866-255-6508 to authorize your order.';
      } else {
        return undefined;
      }
    default:
      return undefined;
  }
};

const mapBaseOrder = (o: PwpApiOrderResponse | OrderStatusApiOrderResponse) => {
  return {
    targetedShipDate: o.targetedShipDate,
    deliveryDate: o.deliveryDate,
    trackingNumber: o.trackingNumber,
    trackingNumberUrl: o.trackingNumberUrl,
    shipmentTypeId: o.shipmentTypeId,
    shipmentTypeName: o.shipmentTypeName,
    shippingAddress: mapAddress(
      o.shippingAddressId,
      o.shippingAddress1,
      o.shippingAddress2,
      o.shippingCity,
      o.shippingState,
      o.shippingZip
    ),
    mailClass: o.mailClass,
    isShipment: typeof o.isShipment === 'boolean' ? o.isShipment : true,
    isFutureOrder: isTwoYearsFromNow(o.targetedShipDate),
    shipmentDescription: o.shipmentDescription ?? o.shipmentTypeName,
    generationType: o.generationType,
    orderType: o.orderType,
  };
};

const mapPwpOrder = (o: PwpApiOrderResponse): Order => {
  const baseOrder = mapBaseOrder(o);
  const status = o.status;

  const order: Order = {
    ...baseOrder,
    id: o.referenceNumber,
    status,
    originatingReorderId: o.reorderCardId,
    needsAuthorization:
      status === PwpOrderStatus.Authorize && o.generationType !== 'MC' && o.orderType !== 'WORKFLOW_ORDER',
    needsReconsent: false,
    isShipped: status === PwpOrderStatus.Shipped || status === PwpOrderStatus.Delivered,
    isDelivered: status === PwpOrderStatus.Delivered,
    estimatedEarliestConsentDate: o.consentDate,
    isFutureConsent: isTwoYearsFromNow(o.consentDate),
  };

  order.statusDescription = computeStatusDescription(order);
  return order;
};

const mapOrderStatusOrder = (o: OrderStatusApiOrderResponse): Order => {
  const baseOrder = mapBaseOrder(o);
  const status = o.oneDotStatus;

  const detailedStatus = o.fiveDotStatus
    .filter((s) => !!s.statusName)
    .map((s) => ({
      name: s.statusName!,
      value: s.internalDetail,
      description: s.externalDetail,
      isCompleted: s.statusFlag === YesOrNo.Y,
    }));

  const order: Order = {
    ...baseOrder,
    id: baseOrder.isShipment ? o.shipmentReferenceNumber || 0 : o.cardReferenceNumber || 0,
    status,
    detailedStatus,
    originatingReorderId: o.cardReferenceNumber,
    needsAuthorization:
      status === OneDotOrderStatus.ReadyToAuthorize && o.generationType !== 'MC' && o.orderType !== 'WORKFLOW_ORDER',
    needsReconsent:
      detailedStatus.find((ds) => ds.name === DetailedOrderStatusName.Consent)?.value ===
      ConsentStatusValue.NeedsReconsent,
    isShipped: status === OneDotOrderStatus.Shipped || status === OneDotOrderStatus.Delivered,
    isDelivered: status === OneDotOrderStatus.Delivered,
    estimatedEarliestConsentDate: o.estimatedEarliestConsentDate || undefined,
    isFutureConsent: isTwoYearsFromNow(o.estimatedEarliestConsentDate),
  };

  order.statusDescription = computeStatusDescription(order);
  return order;
};

export const mapPwpOrders = (orders: PwpApiOrderResponse[], pharmacyActivity: boolean): Order[] => {
  if (pharmacyActivity) {
    return orders.map((o) => mapPwpOrder(o)).filter((o) => o.id !== 0 && o.shipmentTypeId !== 100);
  } else {
    return orders.map((o) => mapPwpOrder(o)).filter((o) => o.id !== 0);
  }
};

export const mapOrderStatusOrders = (orders: OrderStatusApiOrderResponse[], pharmacyActivity: boolean): Order[] => {
  if (pharmacyActivity) {
    return orders.map((o) => mapOrderStatusOrder(o)).filter((o) => o.id !== 0 && o.shipmentTypeId !== 100);
  } else {
    return orders.map((o) => mapOrderStatusOrder(o)).filter((o) => o.id !== 0);
  }
};
